import { useQueryClient } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  CustomerDocumentProcessingStatusSubscription,
  CustomerDocumentProcessingStatusSubscriptionVariables,
} from "lib/generated/app-service-gql/graphql";
import { EventNames, useTracking } from "lib/tracking";
import { UpdateFileStatus } from "../../../../components/content_library/DocumentUploadProgressProvider";
import { useSubscription } from "../useGraphQlClient";

// adding this so it generates the types
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _customerDocumentProcessingStatusSubscription = graphql(`
  subscription customerDocumentProcessingStatus($operationId: String!) {
    customerDocumentProcessingStatus(operationId: $operationId) {
      operationId
      recordId
      fileName
      status
      failureReason
      totalFiles
      allDocumentsProcessed
    }
  }
`);

type Props = {
  operationId: string;
  updateFileStatus?: UpdateFileStatus;
  enabled: boolean;
  onData?: (
    data: CustomerDocumentProcessingStatusSubscription["customerDocumentProcessingStatus"],
  ) => void;
};

/**
 * Contains the logic for handling the processing status of a
 * customer document. Unified subscription for both integration and manual uploads.
 * @param param0
 * @returns
 */
export function useCustomerDocumentProcessingStatus({
  operationId,
  updateFileStatus,
  enabled,
  onData: customOnData,
}: Props) {
  const client = useQueryClient();
  const { logEvent } = useTracking();

  const { data, ...rest } = useSubscription<
    CustomerDocumentProcessingStatusSubscription,
    CustomerDocumentProcessingStatusSubscriptionVariables
  >(
    `
    subscription customerDocumentProcessingStatus($operationId: String!) {
      customerDocumentProcessingStatus(operationId: $operationId) {
        operationId
        recordId
        fileName
        status
        failureReason
        totalFiles
        allDocumentsProcessed
      }
    }
`,
    {
      operationId,
    },
    {
      enabled: !!operationId && enabled,
      onData: async (data) => {
        const result = data.customerDocumentProcessingStatus;

        // Call custom onData handler if provided (for DocumentUploadProgressProvider)
        if (customOnData) {
          customOnData(result);
          return;
        }

        // Default behavior for legacy manual upload tracking
        const { status, recordId, fileName } = result;

        if (status === "completed") {
          logEvent(EventNames.customerDocumentProcessedSuccessfully, {});

          // Update query cache - invalidate all customer documents queries
          await client.invalidateQueries({
            queryKey: ["customerDocuments"],
            exact: false,
          });

          // Update file status if updateFileStatus is provided
          if (updateFileStatus) {
            updateFileStatus({
              operationId,
              fileName,
              documentId: recordId,
              updates: {
                isProcessed: true,
                isFinished: true,
                documentId: recordId,
                isError: false,
              },
            });
          }
        } else if (status === "failed") {
          logEvent(EventNames.customerDocumentProcessingError, {});

          if (updateFileStatus) {
            updateFileStatus({
              operationId,
              fileName,
              documentId: recordId,
              updates: {
                isError: true,
                isFinished: true,
                isProcessed: false,
                documentId: recordId,
              },
            });
          }
        }
      },
    },
  );
  return { data, ...rest };
}
